import {Component, Input, SimpleChanges} from '@angular/core';
import {Router, RouterService, RouterType} from "../../../../../../../services/back/router.service";
import {Router as NgRouter} from '@angular/router'
import {Network, NetworkInterface, NetworkService} from "../../../../../../../services/back/network.service";
import {of, Subscription, switchMap, tap} from "rxjs";
import {CurrentTenantService} from "../../../../../../../services/front/current-tenant.service";
import {SnackbarService} from "../../../../../../../services/front/snackbar.service";
import {MatDialog} from "@angular/material/dialog";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {filter, map} from "rxjs/operators";
import {RouterDetailService} from "../../../../../../../services/front/router-detail.service";
import {
	INFRASTRUCTURE_NETWORKS_URL,
} from "../../../../../../../models/home/navigation.model";
import {AddNetworksToRouterComponent} from "./add-networks-to-router/add-networks-to-router.component";
import {DesignSystemModule} from "../../../../../../design-system/design-system.module";
import {MiniButtonModule} from "../../../../../../global/button/mini-button/mini-button.module";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {ipAddressValidator} from "../../../../../../../utils/forms.utils";
import {NgIf} from "@angular/common";
import {ChipsModule} from "../../../../../../global/chips/chips/chips.module";
import {ButtonWidgetComponent} from "../../../../../../design-system/button-widget/button-widget.component";

@Component({
  selector: 'app-router-networks',
  standalone: true,
	imports: [
		DesignSystemModule,
		MiniButtonModule,
		TranslateModule,
		NgIf,
		ChipsModule,
		ButtonWidgetComponent
	],
  templateUrl: './router-networks.component.html',
  styleUrl: './router-networks.component.scss'
})
export class RouterNetworksComponent {
	@Input() router: Router;
	@Input() networks: NetworkInterface[];

	tenantId: string;
	isEditor: boolean;

	tenantNetworks: Network[] = [];

	interfacesForm: FormGroup;
	interfacesArray: FormArray;

	subscription: Subscription = new Subscription();

	constructor(private fb: FormBuilder,
				private ngRouter: NgRouter,
				private routerService: RouterService,
				private routerDetailService: RouterDetailService,
				private currentTenantService: CurrentTenantService,
				private snackbarService: SnackbarService,
				private networkService: NetworkService,
				private dialog: MatDialog,
				private translate: TranslateService) {
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['networks']) {
			this.buildForm();
		}
	}

	ngOnInit() {
		this.subscription.add(this.currentTenantService.getCurrentTenantChanges()
			.pipe(tap(tenant => {
				this.tenantId = tenant.configuration.id;
				this.isEditor = tenant.configuration.role !== 'read_only';
			}))
			.subscribe());
	}

	buildForm(): void {
		this.interfacesForm = this.fb.group({
			interfaces: this.fb.array([])
		});

		this.interfacesArray = this.interfacesForm.get('interfaces') as FormArray;

		this.networks.forEach(network => {
			this.interfacesArray.push(
				this.fb.group({
					interface: [network.networkInterface, [ipAddressValidator()]]
				})
			);
		});
	}

	openLinkNetworksToRouter() {
		of(this.tenantNetworks)
			.pipe(
				switchMap(networks => !networks?.length
					? this.networkService.getTenantNetworks(this.tenantId)
					: of(networks)),
				tap(networks => this.tenantNetworks = networks),
				map(networks => this.buildAddNetworksToRouterDialogData(networks)),
				switchMap(data => this.dialog.open(AddNetworksToRouterComponent, {
					position: {right: '162.5px'},
					width: '475px',
					data: {routerId: this.router.id, networks: data}
				}).afterClosed()),
				filter(result => result && result.success),
				tap(() => this.snackbarService.show(this.translate.instant('page.infrastructure.router.networkSuccessfullyAdded'))))
			.subscribe(() => this.routerDetailService.refreshRouter());
	}

	private buildAddNetworksToRouterDialogData(networks: Network[]): AddNetworksToRouterDialogData[] {
		return networks.map(r => ({
			network: r,
			selected: this.networks.some(network => network.network.id === r.id)
		})).sort((a, b) => {
			if (a.selected === b.selected) {
				return a.network.name.localeCompare(b.network.name);
			}
			return a.selected ? -1 : 1;
		})
	}

	goToNetwork(networkId: string) {
		this.currentTenantService.getCurrentTenantId().subscribe(tenantId => {
			this.ngRouter.navigate([INFRASTRUCTURE_NETWORKS_URL], {
				queryParams: {
					tenant: tenantId,
					networkId: networkId
				}
			});
		});
	}

	resetNetworkInterface(networkId: string): void {
		const form = {
			networkInterface: undefined
		}
		this.routerService.updateRouterNetworkInterface(this.tenantId, this.router.id, networkId, form)
			.subscribe((result) => {
				this.displayUpdatedSucceed(result)
				this.routerDetailService.refreshRouter()
			});
	}

	updateRouterNetworkInterface(networkId: string, index: number): void {
		const networkInterface = this.getInterfaceControlAt(index);
		if (networkInterface.invalid || networkInterface.value === this.networks[index].networkInterface) {
			return;
		}
		const form = {
			networkInterface: networkInterface.value
		}
		this.routerService.updateRouterNetworkInterface(this.tenantId, this.router.id, networkId, form)
			.subscribe((result) => {
				this.displayUpdatedSucceed(result)
				this.routerDetailService.refreshRouter()
			});
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackbarService.show(this.translate.instant('page.application.detail.update.success'));
		} else {
			this.snackbarService.show(this.translate.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	get interfacesControls() {
		return (this.interfacesForm.get('interfaces') as FormArray).controls;
	}

	getInterfaceControlAt(index: number): FormControl {
		return this.interfacesControls[index].get('interface') as FormControl;
	}

	ngOnDestroy() {
		this.subscription.unsubscribe()
	}

	protected readonly RouterType = RouterType;
}

export interface AddNetworksToRouterDialogData {
	network: Network;
	selected: boolean;
}
